import { Component, computed, effect, input, output, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  formatTableCell,
  isEmptyTable,
  type TableColumn,
  type TableModel,
  type TableRow,
} from '@shared/utils/table-model';
import { moveColumn, pickColumns, pivotTable, transposeTable } from '@shared/utils/table-transform.util';

const I18N = 'shared.dataTable';

/**
 * Tabela compartilhada pelas telas de visualização (DataCuidados, Nuvem de Perguntas,
 * DataStories). Renderiza um `TableModel` com ordenação, paginação opcional, transposição
 * e seleção/reordenação de colunas.
 *
 * A cor de destaque vem por `accent` porque o DataCuidados usa a paleta própria (laranja
 * ou roxo, conforme a dimensão) enquanto as demais telas seguem `--primary`.
 */
@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [TranslocoPipe],
  // Coluna flex para a caixa de rolagem crescer até o limite e o cabeçalho grudar nela.
  host: { class: 'flex min-h-0 flex-col' },
  template: `
    @if (canCustomize()) {
      <div class="mb-2 flex flex-wrap items-center justify-end gap-2">
        @if (canPivot()) {
          <button
            type="button"
            (click)="togglePivot()"
            [attr.aria-pressed]="pivoted()"
            class="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium transition"
            [style.color]="pivoted() ? accent() : null"
            [style.border-color]="pivoted() ? accent() : null"
            [class.text-muted-foreground]="!pivoted()"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
              <rect width="18" height="18" x="3" y="3" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" />
            </svg>
            {{ pivotLabel() }}
          </button>
        }

        @if (transposable()) {
          <button
            type="button"
            (click)="toggleTransposed()"
            [attr.aria-pressed]="transposed()"
            class="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium transition"
            [style.color]="transposed() ? accent() : null"
            [style.border-color]="transposed() ? accent() : null"
            [class.text-muted-foreground]="!transposed()"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
              <path d="m16 3 4 4-4 4" /><path d="M20 7H4" /><path d="m8 21-4-4 4-4" /><path d="M4 17h16" />
            </svg>
            {{ i18n + '.transpose' | transloco }}
          </button>
        }

        @if (showColumnPicker()) {
          <div class="relative">
            <button
              type="button"
              (click)="pickerOpen.set(!pickerOpen())"
              [attr.aria-expanded]="pickerOpen()"
              class="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
                <rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18" /><path d="M15 3v18" />
              </svg>
              {{ i18n + '.columns' | transloco }}
              <span class="tabular-nums">({{ visibleKeys().length }}/{{ allKeys().length }})</span>
            </button>

            @if (pickerOpen()) {
              <div
                class="absolute right-0 top-full z-30 mt-1 w-64 rounded-xl border border-border bg-card p-2 shadow-lg"
                role="group"
                [attr.aria-label]="i18n + '.columns' | transloco"
              >
                @for (key of orderedKeys(); track key) {
                  <div class="flex items-center gap-1 rounded-lg px-1 py-1 hover:bg-muted/60">
                    <label class="flex min-w-0 flex-1 items-center gap-2 text-xs text-foreground">
                      <input
                        type="checkbox"
                        [checked]="visibleKeys().includes(key)"
                        [disabled]="visibleKeys().length === 1 && visibleKeys().includes(key)"
                        (change)="toggleColumn(key)"
                        class="h-3.5 w-3.5 shrink-0 disabled:opacity-40"
                        [style.accent-color]="accent()"
                      />
                      <span class="truncate">{{ labelFor(key) }}</span>
                    </label>
                    <button
                      type="button"
                      (click)="shiftColumn(key, -1)"
                      [disabled]="orderedKeys().indexOf(key) === 0"
                      [attr.aria-label]="i18n + '.moveUp' | transloco"
                      class="rounded p-1 text-muted-foreground transition hover:bg-muted disabled:opacity-30"
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3">
                        <path d="m18 15-6-6-6 6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      (click)="shiftColumn(key, 1)"
                      [disabled]="orderedKeys().indexOf(key) === orderedKeys().length - 1"
                      [attr.aria-label]="i18n + '.moveDown' | transloco"
                      class="rounded p-1 text-muted-foreground transition hover:bg-muted disabled:opacity-30"
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                  </div>
                }
                <button
                  type="button"
                  (click)="resetColumns()"
                  class="mt-1 w-full rounded-lg px-2 py-1.5 text-left text-[11px] font-medium text-muted-foreground transition hover:bg-muted/60"
                >
                  {{ i18n + '.reset' | transloco }}
                </button>
              </div>
            }
          </div>
        }
      </div>
    }

    <div
      class="min-h-0 flex-1 overflow-auto rounded-lg border border-border"
      [style.max-height]="maxHeight() || null"
    >
      <table class="w-full min-w-[480px] border-collapse text-sm">
        @if (caption()) {
          <caption class="sr-only">{{ caption() }}</caption>
        }
        <thead class="sticky top-0 z-10 bg-muted/70 backdrop-blur">
          <tr>
            @for (column of viewModel().columns; track column.key) {
              <th
                scope="col"
                class="border-b border-border px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                [class.text-right]="column.numeric"
              >
                @if (sortable()) {
                  <button
                    type="button"
                    (click)="sortBy(column.key)"
                    class="inline-flex items-center gap-1 transition hover:text-foreground"
                    [style.color]="sortKey() === column.key ? accent() : null"
                  >
                    {{ column.label }}
                    @if (sortKey() === column.key) {
                      <span aria-hidden="true">{{ sortOrder() === 1 ? '▲' : '▼' }}</span>
                    }
                  </button>
                } @else {
                  {{ column.label }}
                }
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (row of pagedRows(); track $index) {
            <tr class="even:bg-muted/30">
              @for (column of viewModel().columns; track column.key) {
                <td
                  class="border-b border-border/60 px-4 py-2 text-foreground"
                  [class.text-right]="column.numeric"
                  [class.tabular-nums]="column.numeric"
                >
                  {{ cell(column, row) }}
                </td>
              }
            </tr>
          } @empty {
            <tr>
              <td
                [attr.colspan]="viewModel().columns.length"
                class="px-4 py-8 text-center text-sm text-muted-foreground"
              >
                {{ emptyLabel() || (i18n + '.empty' | transloco) }}
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    @if (pageCount() > 1) {
      <div class="mt-3 flex items-center justify-center gap-3 text-xs text-muted-foreground">
        <button
          type="button"
          (click)="setPage(currentPage() - 1)"
          [disabled]="currentPage() === 0"
          class="rounded-full border border-border px-3 py-1 transition disabled:opacity-40"
        >
          {{ i18n + '.previous' | transloco }}
        </button>
        <span class="tabular-nums">{{ currentPage() + 1 }} / {{ pageCount() }}</span>
        <button
          type="button"
          (click)="setPage(currentPage() + 1)"
          [disabled]="currentPage() >= pageCount() - 1"
          class="rounded-full border border-border px-3 py-1 transition disabled:opacity-40"
        >
          {{ i18n + '.next' | transloco }}
        </button>
      </div>
    }
  `,
})
export class DataTableComponent {
  readonly model = input.required<TableModel>();
  /** `0` desliga a paginação e mostra todas as linhas. */
  readonly pageSize = input(0);
  readonly sortable = input(true);
  readonly transposable = input(true);
  readonly showColumnPicker = input(true);
  readonly caption = input('');
  readonly emptyLabel = input('');
  /**
   * Altura máxima da caixa de rolagem (valor CSS). Vazio = sem limite, a página rola.
   * Para preencher um container de altura fixa, use `class="h-full"` no host em vez disto.
   */
  readonly maxHeight = input('');
  /** Cor dos elementos ativos (ordenação, transposição). */
  readonly accent = input('hsl(var(--primary))');
  /**
   * Cruzamento: coluna cujos valores viram colunas (ex.: `ano`) e coluna que preenche as
   * células (ex.: `valor`). Só aparece quando as duas são informadas e o cruzamento é
   * possível sem agregar nada.
   */
  readonly pivotKey = input('');
  readonly measureKey = input('');
  readonly pivotLabel = input('');
  /** Comparador extra para ordens não alfabéticas (ex.: faixa etária). */
  readonly compare = input<((a: unknown, b: unknown, key: string) => number | null) | null>(null);

  /** Modelo efetivamente exibido — é o que o export deve levar. */
  readonly viewModelChange = output<TableModel>();

  protected readonly i18n = I18N;
  protected readonly transposed = signal(false);
  protected readonly pivoted = signal(false);
  protected readonly pickerOpen = signal(false);
  protected readonly sortKey = signal('');
  protected readonly sortOrder = signal<1 | -1>(1);
  protected readonly page = signal(0);

  /** `null` enquanto o usuário não reordena — assim novas colunas do servidor aparecem sozinhas. */
  private readonly columnOrder = signal<string[] | null>(null);
  private readonly hiddenKeys = signal<ReadonlySet<string>>(new Set());

  protected readonly canCustomize = computed(
    () => !isEmptyTable(this.model()) && (this.transposable() || this.showColumnPicker() || this.canPivot()),
  );

  /** `null` quando o cruzamento não é possível — o botão nem aparece. */
  private readonly pivotModel = computed(() => {
    const pivotKey = this.pivotKey();
    const measureKey = this.measureKey();
    if (!pivotKey || !measureKey || isEmptyTable(this.model())) return null;
    return pivotTable(this.model(), pivotKey, measureKey);
  });

  protected readonly canPivot = computed(() => !!this.pivotModel());

  private readonly baseModel = computed(() => {
    if (this.pivoted()) return this.pivotModel() ?? this.model();
    return this.transposed() ? transposeTable(this.model()) : this.model();
  });

  protected readonly allKeys = computed(() => this.baseModel().columns.map((column) => column.key));

  protected readonly orderedKeys = computed(() => {
    const order = this.columnOrder();
    const all = this.allKeys();
    if (!order) return all;
    // Mantém a ordem escolhida e acrescenta ao fim colunas que apareceram depois.
    const known = order.filter((key) => all.includes(key));
    return [...known, ...all.filter((key) => !known.includes(key))];
  });

  protected readonly visibleKeys = computed(() => {
    const hidden = this.hiddenKeys();
    const visible = this.orderedKeys().filter((key) => !hidden.has(key));
    return visible.length ? visible : this.allKeys();
  });

  readonly viewModel = computed<TableModel>(() => {
    const picked = pickColumns(this.baseModel(), this.visibleKeys());
    const key = this.sortKey();
    if (!key || !picked.columns.some((column) => column.key === key)) return picked;

    const order = this.sortOrder();
    const custom = this.compare();
    const column = picked.columns.find((item) => item.key === key)!;
    const rows = [...picked.rows].sort((a, b) => {
      const overridden = custom?.(a[key], b[key], key);
      if (overridden != null) return order * overridden;
      return order * defaultCompare(a[key], b[key], column.numeric === true);
    });
    return { columns: picked.columns, rows };
  });

  protected readonly pageCount = computed(() => {
    const size = this.pageSize();
    if (size <= 0) return 1;
    return Math.max(1, Math.ceil(this.viewModel().rows.length / size));
  });

  protected readonly currentPage = computed(() => Math.min(this.page(), this.pageCount() - 1));

  protected readonly pagedRows = computed(() => {
    const size = this.pageSize();
    const rows = this.viewModel().rows;
    if (size <= 0) return rows;
    const start = this.currentPage() * size;
    return rows.slice(start, start + size);
  });

  constructor() {
    // Reseta o estado local quando a página troca de conjunto de dados.
    effect(() => {
      this.model();
      this.resetView();
    });

    effect(() => this.viewModelChange.emit(this.viewModel()));
  }

  /** A primeira coluna costuma vir sem cabeçalho; no seletor ela precisa de algum rótulo. */
  protected labelFor(key: string): string {
    return this.baseModel().columns.find((column) => column.key === key)?.label || '—';
  }

  protected cell(column: TableColumn, row: TableRow): string {
    return formatTableCell(column, row);
  }

  protected toggleTransposed(): void {
    this.transposed.set(!this.transposed());
    // Cruzar e transpor competem pelo mesmo eixo; manter os dois juntos confunde mais que ajuda.
    this.pivoted.set(false);
    this.resetView();
  }

  protected togglePivot(): void {
    this.pivoted.set(!this.pivoted());
    this.transposed.set(false);
    this.resetView();
  }

  private resetView(): void {
    this.resetColumns();
    this.sortKey.set('');
    this.page.set(0);
  }

  protected toggleColumn(key: string): void {
    const hidden = new Set(this.hiddenKeys());
    if (hidden.has(key)) {
      hidden.delete(key);
    } else {
      // Nunca esconde a última coluna visível — sobraria uma tabela vazia.
      if (this.visibleKeys().length <= 1) return;
      hidden.add(key);
    }
    this.hiddenKeys.set(hidden);
  }

  protected shiftColumn(key: string, direction: -1 | 1): void {
    this.columnOrder.set(moveColumn(this.orderedKeys(), key, direction));
  }

  protected resetColumns(): void {
    this.columnOrder.set(null);
    this.hiddenKeys.set(new Set());
  }

  protected sortBy(key: string): void {
    if (this.sortKey() === key) {
      this.sortOrder.set(this.sortOrder() === 1 ? -1 : 1);
    } else {
      this.sortKey.set(key);
      this.sortOrder.set(1);
    }
    this.page.set(0);
  }

  protected setPage(page: number): void {
    this.page.set(Math.max(0, Math.min(page, this.pageCount() - 1)));
  }
}

function defaultCompare(a: unknown, b: unknown, numeric: boolean): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  if (numeric) {
    const na = Number(String(a).replace(',', '.'));
    const nb = Number(String(b).replace(',', '.'));
    if (Number.isFinite(na) && Number.isFinite(nb)) return na < nb ? -1 : na > nb ? 1 : 0;
  }
  return String(a).localeCompare(String(b), 'pt-BR', { numeric: true });
}
